import React, { FC } from 'react';
import {
  Box,
  Grid,
  Skeleton,
  Stack,
  useMediaQuery,
  useTheme,
} from '@mui/material';

export const JournalSectionSkeleton: FC = () => {
  const theme = useTheme();
  const isLGAndHigher = useMediaQuery(theme.breakpoints.up('lg'));
  const isMD = useMediaQuery(theme.breakpoints.only('md'));
  const isSMAndBelow = useMediaQuery(theme.breakpoints.down('md'));

  const getResponsiveConfig = () => {
    if (isLGAndHigher) {
      return {
        padding: theme.spacing(4),
        cellHeight: 60,
        headerHeight: 40,
        fontSize: '14px',
      };
    }
    if (isMD) {
      return {
        padding: theme.spacing(3),
        cellHeight: 50,
        headerHeight: 36,
        fontSize: '12px',
      };
    }
    if (isSMAndBelow) {
      return {
        padding: theme.spacing(2),
        cellHeight: 40,
        headerHeight: 32,
        fontSize: '10px',
      };
    }
    // Fallback
    return {
      padding: theme.spacing(4),
      cellHeight: 60,
      headerHeight: 40,
      fontSize: '14px',
    };
  };

  const config = getResponsiveConfig();
  const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  const weeksCount = 6; // Typical calendar has 6 rows

  return (
    <Box
      sx={{
        width: '100%',
        height: '100%',
        padding: config.padding,
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* Calendar Header */}
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        sx={{ mb: 3 }}
      >
        <Skeleton variant="circular" width={32} height={32} />
        <Skeleton variant="text" width={120} height={config.headerHeight} />
        <Skeleton variant="circular" width={32} height={32} />
      </Stack>

      {/* Days of Week Header */}
      <Grid container spacing={1} sx={{ mb: 1 }}>
        {dayNames.map((day, index) => (
          <Grid size={{ xs: 12 / 7 }} key={index}>
            <Box
              sx={{
                textAlign: 'center',
                py: 1,
              }}
            >
              <Skeleton
                variant="text"
                width="60%"
                height={20}
                sx={{ margin: '0 auto' }}
              />
            </Box>
          </Grid>
        ))}
      </Grid>

      {/* Calendar Grid */}
      <Box sx={{ flex: 1 }}>
        {Array.from({ length: weeksCount }).map((_, weekIndex) => (
          <Grid container spacing={1} key={weekIndex} sx={{ mb: 1 }}>
            {Array.from({ length: 7 }).map((_, dayIndex) => (
              <Grid size={{ xs: 12 / 7 }} key={dayIndex}>
                <Box
                  sx={{
                    height: config.cellHeight,
                    border: `1px solid ${theme.palette.divider}`,
                    borderRadius: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    p: 1,
                    position: 'relative',
                  }}
                >
                  {/* Date number skeleton */}
                  <Skeleton
                    variant="text"
                    width={16}
                    height={16}
                    sx={{ alignSelf: 'flex-start' }}
                  />

                  {/* Optional event indicators for some cells */}
                  {(weekIndex === 1 && (dayIndex === 2 || dayIndex === 5)) ||
                  (weekIndex === 2 && dayIndex === 1) ||
                  (weekIndex === 3 && (dayIndex === 3 || dayIndex === 6)) ? (
                    <Box sx={{ mt: 0.5 }}>
                      <Skeleton
                        variant="rectangular"
                        width="80%"
                        height={8}
                        sx={{ borderRadius: 1 }}
                      />
                    </Box>
                  ) : null}
                </Box>
              </Grid>
            ))}
          </Grid>
        ))}
      </Box>
    </Box>
  );
};
